import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Spinner,
  Input,
} from '../../components/design-system';
import type { Category } from '../../types';

type EditingCategory = { id: string; name: string; slug: string; sort_order: number };

export function AdminCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditingCategory | null>(null);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState<{ cat: Category; replacement: string } | null>(null);

  async function load() {
    const { data } = await supabase.from('categories').select('*').order('sort_order');
    setCategories((data ?? []) as Category[]);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function handleAdd() {
    if (!newName.trim()) return;
    setSaving(true);
    const slug = newName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const maxOrder = categories.reduce((m, c) => Math.max(m, c.sort_order), -1);
    await supabase.from('categories').insert({ name: newName.trim(), slug, sort_order: maxOrder + 1 });
    setNewName('');
    setSaving(false);
    await load();
  }

  async function handleSaveEdit() {
    if (!editing) return;
    setSaving(true);
    await supabase.from('categories').update({ name: editing.name, slug: editing.slug }).eq('id', editing.id);
    setEditing(null);
    setSaving(false);
    await load();
  }

  async function handleDeactivate() {
    if (!deactivating) return;
    setSaving(true);
    const { cat, replacement } = deactivating;

    // Migrate event_library items
    await supabase.from('event_library').update({ category_slug: replacement }).eq('category_slug', cat.slug);

    // Migrate places inside itineraries JSONB — need an RPC or manual update
    // For now, we'll handle this via a simple approach: fetch all itineraries, update in JS
    const { data: itineraries } = await supabase.from('itineraries').select('id, content');
    if (itineraries) {
      for (const it of itineraries) {
        const content = it.content as { days?: Array<{ places?: Array<{ category?: string }> }> };
        if (!content?.days) continue;
        let changed = false;
        for (const day of content.days) {
          if (!day.places) continue;
          for (const place of day.places) {
            if (place.category === cat.slug) {
              place.category = replacement;
              changed = true;
            }
          }
        }
        if (changed) {
          await supabase.from('itineraries').update({ content }).eq('id', it.id);
        }
      }
    }

    await supabase.from('categories').update({ active: false }).eq('id', cat.id);
    setDeactivating(null);
    setSaving(false);
    await load();
  }

  async function handleReorder(id: string, direction: -1 | 1) {
    const idx = categories.findIndex((c) => c.id === id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= categories.length) return;
    const a = categories[idx];
    const b = categories[swapIdx];
    await Promise.all([
      supabase.from('categories').update({ sort_order: b.sort_order }).eq('id', a.id),
      supabase.from('categories').update({ sort_order: a.sort_order }).eq('id', b.id),
    ]);
    await load();
  }

  const activeCats = categories.filter((c) => c.active);
  const inactiveCats = categories.filter((c) => !c.active);

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Categories</h1>
        <p className="text-app-muted text-sm mb-8">Manage place categories used across all handbooks and event libraries.</p>

        {/* Add new */}
        <div className="flex gap-3 mb-8">
          <div className="flex-1">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New category name"
              onKeyDown={(e) => { if (e.key === 'Enter') void handleAdd(); }}
            />
          </div>
          <Button onClick={() => void handleAdd()} disabled={saving || !newName.trim()}>Add</Button>
        </div>

        {/* Active categories */}
        <div className="flex flex-col gap-2">
          {activeCats.map((cat, idx) => (
            <div key={cat.id} className="bg-app-card border border-app-border rounded-xl px-4 py-3 flex items-center gap-3">
              {editing?.id === cat.id ? (
                <>
                  <input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className="flex-1 bg-app-bg border border-app-border rounded-lg px-3 py-1.5 text-sm text-app-fg focus:outline-none"
                  />
                  <Button size="sm" onClick={() => void handleSaveEdit()} disabled={saving}>Save</Button>
                  <button onClick={() => setEditing(null)} className="text-xs text-app-muted hover:text-app-fg">Cancel</button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm font-medium text-app-fg">{cat.name}</span>
                    <span className="text-xs text-app-muted">({cat.slug})</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => void handleReorder(cat.id, -1)}
                      disabled={idx === 0}
                      className="p-1 text-app-muted hover:text-app-fg disabled:opacity-30 transition-colors"
                      title="Move up"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => void handleReorder(cat.id, 1)}
                      disabled={idx === activeCats.length - 1}
                      className="p-1 text-app-muted hover:text-app-fg disabled:opacity-30 transition-colors"
                      title="Move down"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setEditing({ id: cat.id, name: cat.name, slug: cat.slug, sort_order: cat.sort_order })}
                      className="text-xs text-app-muted hover:text-app-fg ml-2"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeactivating({ cat, replacement: activeCats.find((c) => c.id !== cat.id)?.slug ?? '' })}
                      className="text-xs text-crimson/60 hover:text-crimson ml-2"
                    >
                      Remove
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Inactive */}
        {inactiveCats.length > 0 && (
          <div className="mt-8">
            <p className="text-xs uppercase tracking-widest text-app-muted font-medium mb-3">Deactivated</p>
            <div className="flex flex-col gap-2">
              {inactiveCats.map((cat) => (
                <div key={cat.id} className="bg-app-card/50 border border-app-border rounded-xl px-4 py-3 flex items-center gap-3 opacity-60">
                  <span className="text-sm text-app-muted flex-1">{cat.name} ({cat.slug})</span>
                  <button
                    onClick={async () => { await supabase.from('categories').update({ active: true }).eq('id', cat.id); await load(); }}
                    className="text-xs text-success hover:text-success/80"
                  >
                    Reactivate
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Deactivation modal */}
      {deactivating && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => setDeactivating(null)}>
          <div className="bg-app-card border border-app-border rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-app-fg mb-2">Remove "{deactivating.cat.name}"</h3>
            <p className="text-sm text-app-muted mb-4">
              All places using this category will be moved to the replacement you choose below.
            </p>
            <label className="text-sm font-medium text-app-fg-2 mb-1 block">Move places to:</label>
            <select
              value={deactivating.replacement}
              onChange={(e) => setDeactivating({ ...deactivating, replacement: e.target.value })}
              className="w-full rounded-lg border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg mb-4 focus:outline-none focus:border-gold"
            >
              {activeCats.filter((c) => c.id !== deactivating.cat.id).map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeactivating(null)} className="text-sm text-app-muted hover:text-app-fg">Cancel</button>
              <Button onClick={() => void handleDeactivate()} disabled={saving || !deactivating.replacement}>
                {saving ? 'Migrating…' : 'Remove & migrate'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCategories;
